import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import type { Transaction } from '../api/transactions'; // Assuming Transaction type is still in api/transactions.ts

// Interface for data coming from Supabase 'recent_transactions' table
interface DbRecentTransaction {
  txid: string;
  blockindex: number | null;
  blockhash: string | null;
  recipients: number | null;
  amount: number | null;
  timestamp: string | null; // ISO 8601 string
}

// Helper to transform DB transaction to the Transaction type
const transformDbTxToTransactionForList = (dbTx: DbRecentTransaction): Transaction => {
  return {
    txid: dbTx.txid,
    blockindex: dbTx.blockindex ?? 0,
    blockhash: dbTx.blockhash ?? '',
    recipients: dbTx.recipients ?? 0,
    amount: dbTx.amount ?? 0,
    timestamp: dbTx.timestamp ? Math.floor(new Date(dbTx.timestamp).getTime() / 1000) : 0, // Convert ISO to Unix epoch seconds
  };
};

export function useTransactions(count: number = 10) { // Default count can be adjusted
  const [transactions, setTransactions] = useState<Transaction[]>([]); // Initialize with empty array
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let mounted = true;

    async function fetchInitialTransactions() {
      if (!mounted) return;
      setLoading(true);
      setError(null);
      try {
        const { data: dbTxs, error: dbError } = await supabase
          .from('recent_transactions')
          .select('*')
          .order('timestamp', { ascending: false })
          .limit(count);

        if (!mounted) return;

        if (dbError) {
          console.error('Error fetching transactions from Supabase (useTransactions):', dbError);
          setError(new Error('Failed to fetch transactions: ' + dbError.message));
          setTransactions([]); // Ensure it's an empty array on error
        } else if (dbTxs) {
          setTransactions(dbTxs.map(transformDbTxToTransactionForList));
        } else {
          setTransactions([]); // No data found, ensure empty array
        }
      } catch (err) {
        if (!mounted) return;
        console.error('Catch block error fetching transactions (useTransactions):', err);
        setError(err instanceof Error ? err : new Error('An unexpected error occurred'));
        setTransactions([]); // Ensure it's an empty array on catch
      } finally {
        if (mounted) setLoading(false);
      }
    }

    fetchInitialTransactions();

    const channel = supabase
      .channel(`public:recent_transactions:list_count=${count}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'recent_transactions' },
        (payload) => {
          if (!mounted) return;
          console.log('Realtime update received for recent_transactions (for useTransactions hook): ', payload);
          fetchInitialTransactions(); 
        }
      )
      .subscribe((status, err) => {
        if (!mounted) return;
        if (err) {
          console.error('Realtime subscription error for recent_transactions (useTransactions):', err);
        } else if (status === 'SUBSCRIBED') {
          console.log('Successfully subscribed to recent_transactions updates (for useTransactions).');
        }
      });

    return () => {
      mounted = false;
      if (channel) {
        supabase.removeChannel(channel).catch(errRm => console.error("Failed to remove channel for useTransactions", errRm));
      }
    };
  }, [count]);

  console.log('[useTransactions] Returning:', { transactions, loading, error }); // Added for debugging
  return { transactions, loading, error };
}